// mixins.js

import Vue from "vue";
import { env } from "./env";
import { metaObj } from "../config/metadata/metaConfig";
import analytics from "./eventAnalytics/plugin";
import { getConfigurableRoutes } from "../router";
import seoMixin from "./mixins/seo.mixin";
import { appendCountryToTitle } from './metaTitleHelper';
import { matchesExcludeFromIndex } from "./seo";

// Global mixin
const mixins = {
	data() {
		return {
			env,
			routes: [],
			baseUrl: "",
			locale: "",
			_eventListeners: [], // Track event listeners for cleanup
			_timeoutIds: [],     // Track setTimeout IDs for cleanup
			_intervalIds: []     // Track setInterval IDs for cleanup
		};
	},
	created() {
	},
	metaInfo() {
		// I'm generating meta information for the current page
		if (!this.$route) return {};
		const currentCountry = env.country;
		const currentPage = this.$route.name;
		const pageMetaMap = {
			"spribe-game": "avaitorMeta",
			virtual: "virtualsMeta",
			virtuals: "virtualsMeta",
			casino: "casinoMeta",
			"betika-fasta": "betikaFastaMeta",
			promotions: "promotionsMeta",
			"crash-games": "crashGamesMeta",
		};
		const metaKey = pageMetaMap[currentPage];
		const metaConfig = metaObj[currentCountry] && metaObj[currentCountry][metaKey];

		let metaTags = {};
		if (metaConfig) {
			const title = appendCountryToTitle(metaConfig.title, currentCountry);
			metaTags = {
				title: title,
				meta: [
					{
						vmid: "description",
						name: "description",
						content: metaConfig.description,
					},
				],
			};
		}

		// I'm adding hreflang links to the meta tags
		const links = this.hreflangLink();
		metaTags.link = links;

		return metaTags;
	},
	computed: {
		isMiniApp() {
			let vm = this;
			const utmSource = vm.$cookie.get("utm_source") || null;
			return utmSource === vm.env.miniapp.utmSrc;
		},
		isMiniappSupportedNetwork() {
			let vm = this;
			const userNetwork = vm.$store.getters.user.network || "";

			if (!userNetwork) {
				return false;
			}

			const supportedNetworks = vm.env.miniapp.supportedNetworks || [];
			return supportedNetworks.includes(userNetwork);
		},
	},
	methods: {
		/**
		 * Tracks an analytics event using our centralized analytics system.
		 * @param {string} category - The event category
		 * @param {string|null} subcategoryOrEvent - The event subcategory or event name if no subcategory
		 * @param {string|Object} eventOrPayload - The event name or payload if no subcategory
		 * @param {Object|null} payloadOrNull - The payload or null if no subcategory
		 */


		analyticsTrack(
			category,
			subcategoryOrEvent,
			eventOrPayload,
			payloadOrNull
		) {
			if (payloadOrNull !== undefined) {
				// Subcategory is provided
				if (
					analytics[category] &&
					analytics[category][subcategoryOrEvent] &&
					analytics[category][subcategoryOrEvent][eventOrPayload]
				) {
					analytics[category][subcategoryOrEvent][eventOrPayload](
						payloadOrNull
					);
				} else {
					console.warn(
						`Analytics event not found: ${category}.${subcategoryOrEvent}.${eventOrPayload}`
					);
				}
			} else {
				// No subcategory
				if (analytics[category] && analytics[category][subcategoryOrEvent]) {
					analytics[category][subcategoryOrEvent](eventOrPayload);
				} else {
					console.warn(
						`Analytics event not found: ${category}.${subcategoryOrEvent}`
					);
				}
			}
		},
		productAvailable(product) {
			// I'm checking if a specific product is available
			return env.availableProducts.includes(product);
		},
		supportsV2Payments() {
			// I'm checking if V2 payments are supported
			return this.env.availableProducts.includes("paymentsV2");
		},
		hreflangLink() {
			// I'm generating hreflang links for SEO
			let vm = this;
			try {
				if (!vm.env || !vm.env.availableCountries || !Array.isArray(vm.env.availableCountries)) {
					console.warn('Missing environment or available countries for hreflang generation');
					return [];
				}

				const links = [];
				for (let i = 0; i < vm.env.availableCountries.length; i++) {
					const country = vm.env.availableCountries[i];
					if (!country || !country.availableLocales) continue;

					let path = '';
					if (vm.$route && vm.$route.params && vm.$route.params.locale) {
						path = vm.$route.path.replace('/' + vm.$route.params.locale + '/', "");
					} else if (vm.$route) {
						path = vm.$route.path;
					}

					let route = vm.$route ? vm.$route.name : '';

					// Special handling for specific routes
					if (route === "boosted-multibet-events" && vm.env.sababishaProduct && country.code) {
						path = '/' + vm.env.sababishaProduct[country.code];
					}
					if (route === "boosted-multibet-event" && vm.env.sababishaProduct && country.code &&
						vm.$route && vm.$route.params && vm.$route.params.id) {
						path = '/' + vm.env.sababishaProduct[country.code] + '/' + vm.$route.params.id;
					}
					if (route === "shikisha-bet" && vm.env.shikishaProduct && country.code) {
						path = '/' + vm.env.shikishaProduct[country.code];
					}

					for (let j = 0; j < country.availableLocales.length; j++) {
						const locale = country.availableLocales[j];
						const href = vm.env.desktopUrl + '/' + locale + path;
						links.push({
							rel: "alternate",
							hreflang: locale,
							href: href.toLowerCase()
						});
					}
				}

				// Add x-default and canonical links
				if (vm.env.desktopUrl && vm.env.locale && vm.$route) {
					links.push(
						{
							rel: "alternate",
							hreflang: "x-default",
							href: vm.env.desktopUrl + '/' + vm.env.locale + vm.$route.path,
						},
						{
							rel: "canonical",
							href: (vm.env.desktopUrl + '/' + vm.env.locale + vm.$route.path).toLowerCase(),
						}
					);
				}

				return links;
			} catch (error) {
				console.error('Error generating hreflang links:', error);
				return [];
			}
		},
		// Sitemap generation methods
		initBaseUrl() {
			// I'm setting up the base URL for the sitemap
			const isProd = process.env.NODE_ENV === "production";
			this.baseUrl = isProd ? env.websiteUrl : "http://localhost:8080";
			this.locale = process.env.DEFAULT_I18N_LOCALE || "en-ke";

			if (!this.baseUrl.endsWith("/")) {
				this.baseUrl += "/";
			}

			this.baseUrl += `${this.locale}/`;
		},
		generateSitemap() {
			// I'm initializing the routes and baseUrl when the component is created
			this.routes = getConfigurableRoutes();
			this.initBaseUrl();
			// I'm generating the full XML sitemap
			let sitemap = '<?xml version="1.0" encoding="UTF-8"?>\n';
			sitemap +=
				'<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';

			this.routes.forEach((route) => {
				if (this.shouldIncludeRoute(route)) {
					sitemap += this.generateUrlEntry(route);
				}
			});

			sitemap += "</urlset>";
			return sitemap;
		},
		shouldIncludeRoute(route) {
			// I'm determining if a route should be included in the sitemap
			const excludedPaths = [
				"*",
				"login",
				"register",
				"profile",
				"betslip",
				"mybets",
			];

			const excludedPrefixes = ["m/", "live/m/"];

			const path = route.path.startsWith("/")
				? route.path.slice(1)
				: route.path;

			// Also exclude paths from SEO configuration if available
			const seoExcludes = (env.seoConfiguration && env.seoConfiguration.excludeFromIndex) || [];
			if (matchesExcludeFromIndex(route.path, seoExcludes)) {
				return false;
			}

			return (
				!(route.meta && route.meta.requireAuth) &&
				!path.includes(":") &&
				!excludedPaths.includes(path) &&
				!excludedPrefixes.some((prefix) => path.startsWith(prefix))
			);
		},
		generateUrlEntry(route) {
			// I'm generating a single URL entry for the sitemap
			let entry = "  <url>\n";
			entry += `    <loc>${this.baseUrl}${this.getRouteUrl(route)}</loc>\n`;

			const lastMod = this.getLastMod(route);
			if (lastMod) {
				entry += `    <lastmod>${lastMod}</lastmod>\n`;
			}

			const changeFreq = this.getChangeFreq(route);
			if (changeFreq) {
				entry += `    <changefreq>${changeFreq}</changefreq>\n`;
			}

			const priority = this.getPriority(route);
			if (priority !== null) {
				entry += `    <priority>${priority.toFixed(1)}</priority>\n`;
			}

			entry += "  </url>\n";
			return entry;
		},
		getRouteUrl(route) {
			// I'm getting the URL for a route
			let path = route.path.startsWith("/") ? route.path.slice(1) : route.path;
			return path === "" ? "" : path;
		},
		getLastMod(route) {
			// I'm getting the last modification date for a route
			// For now, I'm just returning the current date
			return new Date().toISOString().split("T")[0];
		},
		getChangeFreq(route) {
			// I'm determining the change frequency for a route
			if (route.path === "" || route.path === "/") return "daily";
			if (route.path.startsWith("live")) return "always";
			if (route.path.startsWith("s/")) return "hourly";
			if (route.path.startsWith("jackpots")) return "daily";
			if (route.path.startsWith("virtuals")) return "hourly";
			if (route.path.startsWith("casino")) return "weekly";
			return "monthly";
		},
		getPriority(route) {
			// I'm determining the priority for a route
			if (route.path === "" || route.path === "/") return 1.0;
			if (route.path.startsWith("live")) return 0.9;
			if (route.path.startsWith("s/")) return 0.8;
			if (route.path.startsWith("jackpots")) return 0.7;
			if (route.path.startsWith("virtuals")) return 0.6;
			if (route.path.startsWith("casino")) return 0.5;

			// Check for priority pages in SEO configuration
			if (env.seoConfiguration &&
				env.seoConfiguration.priorityPages &&
				Array.isArray(env.seoConfiguration.priorityPages)) {

				const priorityPages = env.seoConfiguration.priorityPages;
				const normalizedPath = route.path.startsWith('/') ? route.path : '/' + route.path;

				// Check if this route is in the priority pages
				for (let i = 0; i < priorityPages.length; i++) {
					const priorityPath = priorityPages[i];
					if (normalizedPath === priorityPath) {
						// Give higher priority based on position in the array (earlier = higher priority)
						return 0.9 - (i * 0.1);
					}
				}
			}

			return 0.4;
		},
		// Safe way to add event listeners that will be automatically removed
		addEventListenerWithCleanup(target, type, listener, options) {
			if (!target) return;

			target.addEventListener(type, listener, options);
			this._eventListeners.push({ target, type, listener, options });
		},
		// Safe setTimeout that will be automatically cleared
		setTimeoutWithCleanup(callback, delay) {
			const id = setTimeout(callback, delay);
			this._timeoutIds = this._timeoutIds || [];
			this._timeoutIds.push(id);
			return id;
		},
		// Safe setInterval that will be automatically cleared
		setIntervalWithCleanup(callback, delay) {
			const id = setInterval(callback, delay);
			this._intervalIds = this._intervalIds || [];
			this._intervalIds.push(id);
			return id;
		},
		// Clear a specific timeout
		clearTimeoutWithCleanup(id) {
			if (!id) return;

			clearTimeout(id);
			this._timeoutIds = this._timeoutIds || [];
			this._timeoutIds = this._timeoutIds.filter(timeoutId => timeoutId !== id);
		},
		// Clear a specific interval
		clearIntervalWithCleanup(id) {
			if (!id) return;

			clearInterval(id);
			this._intervalIds = this._intervalIds || [];
			this._intervalIds = this._intervalIds.filter(intervalId => intervalId !== id);
		},
		waitForZendesk(callback, timeout = 5000) {
			const interval = 50;
			let waited = 0;

			const check = () => {
				if (window.zE && typeof window.zE === 'function') {
					callback();
				} else if (waited < timeout) {
					waited += interval;
					setTimeout(check, interval);
				} else {
					console.warn('Zendesk Web Widget not loaded in time.');
				}
			};

			check();
		},
		isValidContent(content){
			const now = new Date();
			const hasStartDate = content.start_date && content.start_date.trim() !== '';
			const hasEndDate = content.end_date && content.end_date.trim() !== '';

			// if neither date exists, treat the content as having no expiry; always valid
			if(!hasStartDate && !hasEndDate){
				return true;
			}

			// check start_date validity
			if(hasStartDate){
				const startDate = new Date(content.start_date);
				// valid start date must be in the past
				if(now < startDate){
					return false;
				}
			}

			if(hasEndDate){
				const endDate = new Date(content.end_date);
				// valid end date must be in the future
				if(now > endDate){
					return false;
				}
			}

			return true; // current date is between start date and end date
		},
		getValidContent(contentsArray){
			let vm = this;
			contentsArray = Array.isArray(contentsArray) ? contentsArray : [];

			return contentsArray.filter(item => vm.isValidContent(item));
		},
		isContentActive(content){
			const now = new Date();
			const hasStartDate = content.start_date && content.start_date.trim() !== '';


			// check start_date validity
			if(hasStartDate){
				const startDate = new Date(content.start_date);
				// valid start date must be in the past
				if(now < startDate){
					return false;
				}
			}

			return true; // has no start date, should always run
		},
	},
	// Add component lifecycle hooks for cleanup
	beforeDestroy() {
		// Clean up all event listeners added through our wrapper method
		if (this._eventListeners && Array.isArray(this._eventListeners)) {
			this._eventListeners.forEach(({ target, type, listener, options }) => {
				if (target && typeof target.removeEventListener === 'function') {
					try {
						target.removeEventListener(type, listener, options);
					} catch (e) {
						console.error('Error removing event listener:', e);
					}
				}
			});
			this._eventListeners = [];
		}

		// Clear any component-specific timeouts or intervals
		if (this._timeoutIds && Array.isArray(this._timeoutIds)) {
			this._timeoutIds.forEach(id => {
				if (id) clearTimeout(id);
			});
			this._timeoutIds = [];
		}

		if (this._intervalIds && Array.isArray(this._intervalIds)) {
			this._intervalIds.forEach(id => {
				if (id) clearInterval(id);
			});
			this._intervalIds = [];
		}
	}
};

// I'm applying the global mixins
Vue.mixin(mixins);

const RegisterMixins = () => {
	// I'm creating a function to register the mixins
	return new Promise((resolve, reject) => {
		try {
			// Register global mixin
			Vue.mixin(mixins);

			// Create a mapping of component names to the SEO mixin
			// Components can use this by importing and adding to their mixins: [seoMixins]
			Vue.prototype.$seoMixin = seoMixin;

			resolve(true);
		} catch (error) {
			console.error("Error registering mixins:", error);
			reject(error);
		}
	});
};

export { RegisterMixins, seoMixin };
